import React from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { profile } from "../data/profile";

export function SEO({ title, description, image, url, canonical, index = true, breadcrumbs = [], websiteSchema = false, keywords = [], articles = [], pageType = 'WebPage' }) {
  const metaTitle = title ? `${title} | ${profile.seo.siteName}` : profile.seo.siteName;
  const metaDescription = description || profile.seo.description;
  const baseUrl = profile.seo.url?.replace(/\/$/, "");
  const img = image || profile.seo.ogImage;
  const metaImage = img?.startsWith("http") ? img : `${baseUrl}${img}`;
  const metaUrl = url || profile.seo.url;
  const canonicalUrl = canonical || metaUrl;
  const defaultKeywords = [
    // Name variations - CRITICAL for personal SEO
    "Unais Ali", "Unais", "Ali Unais", "Unais Ali EdTech", "Unais Ali UTech", "Unais Ali Founder", "Unais Ali CEO",
    "Unais Ali Technical PM", "Unais Ali Product Manager", "Unais Ali Embedded Systems", "Unais Ali ML Engineer", 
    "Unais Ali IoT", "Unais Ali Engineer", "Unais Ali Portfolio", "Unais Ali Resume", "Unais Ali Blog",
    "Unais Ali Projects", "Unais Ali Research", "Unais Ali EMU", "Unais Ali Eastern Michigan University", 
    "Unais Ali Ypsilanti", "Unais Ali Michigan", "Unais Ali Graduate Student", "Unais Ali Scholarship",
    "Unais Ali Mentor", "Unais Ali STEM", "Unais Ali Pakistan", "Unais Ali LinkedIn",
    // Professional titles & roles
    "EdTech Founder", "EdTech CEO", "STEM Education Leader", "Graduate Student", "Graduate Assistant",
    "Technical Product Manager", "Product Manager", "Technical PM", "Embedded Systems Engineer",
    "Machine Learning Engineer", "ML Engineer", "IoT Engineer", "Computer Vision Engineer", "Full Stack Developer",
    "Student Mentor", "Capstone Mentor", "STEM Mentor", "Education Mentor",
    // UTech Digital Education
    "UTech Digital Education", "UTech Founder", "UTech CEO", "UTech Digital Education Founder",
    "UTech Online Learning", "UTech STEM", "UTech AI Education", "UTech Robotics",
    // Education & Academic
    "STEM Education", "AI Education", "Robotics Education", "Coding for Kids", "EdTech", "Educational Technology",
    "Engineering Management", "Engineering Management EMU", "GameAbove College", "TRUEMU",
    "Eastern Michigan University", "EMU", "Ypsilanti Michigan", "Graduate Assistant EMU",
    "4.0 GPA", "Perfect GPA", "University Graduate Scholarship", "Fully Funded Scholarship",
    // Achievements & Recognition
    "F6S Top 100", "F6S Top 100 Online Learning Platform", "F6S Ranking", "Top Online Learning Platform",
    "Digital Zen", "Mental Wellness Software", "Dare2Design", "STEM Science Festival",
    "Capstone Projects", "Student Mentorship", "Brainwave Control", "OpenBCI",
    // Technical domains
    "electronics engineering", "embedded systems", "embedded software", "firmware", "microcontrollers", "IoT", 
    "edge AI", "computer vision", "machine learning", "deep learning", "DSP", "control systems", "robotics", 
    "Python", "C/C++", "JavaScript", "React", "Node.js", "PyTorch", "ESP32", "Raspberry Pi", "Arduino",
    // Study Abroad & Scholarships
    "SOP Writing", "Statement of Purpose", "Scholarship Application", "F1 Visa", "Study in USA",
    "Fully Funded Scholarship", "US University Admission", "Graduate Assistantship", "TA", "GA", "GRA",
    // Industry keywords
    "automotive", "EV", "ADAS", "autonomous driving", "functional safety",
    // Big tech
    "NVIDIA", "Qualcomm", "Intel", "AMD", "Apple", "Google", "Alphabet", "Amazon", "AWS", "Microsoft", "Azure", "Meta", "OpenAI",
  ];
  const metaKeywords = (keywords.length ? keywords : defaultKeywords).join(", ");

  return (
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={index ? "index,follow" : "noindex,nofollow"} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:secure_url" content={metaImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={metaTitle} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:site_name" content={profile.seo.siteName} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="en_GB" />
      <meta property="og:locale:alternate" content="en_CA" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:image:alt" content={metaTitle} />
      <meta name="twitter:domain" content="unaisali.com" />
      {profile.seo.twitterHandle && (
        <meta name="twitter:site" content={profile.seo.twitterHandle} />
      )}
      {profile.seo.twitterHandle && (
        <meta name="twitter:creator" content={profile.seo.twitterHandle} />
      )}

      {/* Schema.org Person JSON-LD - Enhanced */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: profile.name,
          alternateName: ['Unais', 'Unais Ali Technical PM'],
          url: profile.seo.url,
          image: profile.avatarUrl,
          jobTitle: profile.title,
          email: profile.email ? `mailto:${profile.email}` : undefined,
          address: {
            '@type': 'PostalAddress',
            addressLocality: profile.location?.split(',')[0] || 'Ypsilanti',
            addressRegion: profile.location?.split(',')[1]?.trim() || 'MI',
            addressCountry: 'US'
          },
          alumniOf: {
            '@type': 'EducationalOrganization',
            name: 'Eastern Michigan University',
            url: 'https://www.emich.edu'
          },
          knowsAbout: [
            'STEM Education',
            'EdTech',
            'Educational Technology',
            'AI Education',
            'Robotics Education',
            'Coding for Kids',
            'Engineering Management',
            'Cybersecurity',
            'Applied Computing',
            'Student Mentorship',
            'Capstone Projects',
            'Technical Product Management',
            'Embedded Systems',
            'Machine Learning',
            'Computer Vision',
            'IoT',
            'React.js',
            'Node.js',
            'PyTorch',
            'ESP32',
            'Raspberry Pi',
            'Scholarship Applications',
            'SOP Writing',
            'F1 Visa Process'
          ],
          award: [
            'University Graduate Scholarship Award 2025-2026',
            'TRUEMU Professional Development Badge',
            'F6S Top 100 Online Learning Platform (#21)',
            'Winter Semester Mini Grant for Digital Zen'
          ],
          worksFor: {
            '@type': 'Organization',
            name: 'UTech Digital Education',
            url: profile.social.website
          },
          sameAs: [
            profile.social.github,
            profile.social.linkedin,
            profile.social.twitter,
            profile.social.website,
          ].filter(Boolean),
        })}
      </script>

      {/* Schema.org Organization JSON-LD (for brand entity) */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: profile.seo.siteName,
          url: profile.seo.url,
          logo: profile.avatarUrl,
          founder: {
            '@type': 'Person',
            name: profile.name
          },
          sameAs: [profile.social.github, profile.social.linkedin, profile.social.website].filter(Boolean),
        })}
      </script>
      
      {/* Additional Meta Tags for SEO */}
      <meta name="author" content={profile.name} />
      <meta name="copyright" content={profile.name} />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="coverage" content="worldwide" />
      <meta name="target" content="all" />
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />
      <meta name="format-detection" content="telephone=no" />
      {profile.location && (
        <>
          <meta name="geo.region" content="US-MI" />
          <meta name="geo.placename" content={profile.location.split(',')[0]} />
        </>
      )}
      
      {/* Enhanced Open Graph */}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/png" />
      <meta property="profile:first_name" content="Unais" />
      <meta property="profile:last_name" content="Ali" />
      
      {/* Enhanced Twitter */}
      <meta name="twitter:image:alt" content={metaTitle} />

      {/* Breadcrumbs (if provided) */}
      {breadcrumbs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((crumb, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: crumb.name,
              item: crumb.item,
            })),
          })}
        </script>
      )}

      {/* Website with SearchAction (only once, usually home) */}
      {websiteSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            '@id': `${profile.seo.url}/#website`,
            name: profile.seo.siteName,
            url: profile.seo.url,
            description: metaDescription,
            inLanguage: 'en-US',
            isAccessibleForFree: true,
            publisher: {
              '@type': 'Person',
              '@id': `${profile.seo.url}/#person`,
              name: profile.name
            },
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: `${profile.seo.url}/?q={search_term_string}`
              },
              'query-input': 'required name=search_term_string',
            },
          })}
        </script>
      )}
      
      {/* WebPage Schema for better page indexing */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': pageType === 'CollectionPage' ? 'CollectionPage' : pageType === 'ProfilePage' ? 'ProfilePage' : 'WebPage',
          '@id': `${canonicalUrl}#webpage`,
          url: canonicalUrl,
          name: metaTitle,
          headline: metaTitle,
          description: metaDescription,
          inLanguage: 'en-US',
          isPartOf: {
            '@type': 'WebSite',
            '@id': `${profile.seo.url}/#website`,
            name: profile.seo.siteName,
            url: profile.seo.url
          },
          about: {
            '@type': 'Person',
            '@id': `${profile.seo.url}/#person`,
            name: profile.name
          },
          author: {
            '@type': 'Person',
            '@id': `${profile.seo.url}/#person`,
            name: profile.name
          },
          publisher: {
            '@type': 'Person',
            '@id': `${profile.seo.url}/#person`,
            name: profile.name
          },
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: metaImage,
            width: 1200,
            height: 630,
            caption: metaTitle
          },
          datePublished: new Date().toISOString().split('T')[0],
          dateModified: new Date().toISOString().split('T')[0],
          ...(breadcrumbs.length > 0 && {
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: breadcrumbs.map((crumb, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: crumb.name,
                item: crumb.item
              }))
            }
          })
        })}
      </script>

      {/* Article Schema for Research Papers (citation-rich for Google Scholar & discovery) */}
      {articles.length > 0 && articles.map((article, index) => (
        <script key={`article-${index}`} type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ScholarlyArticle',
            '@id': article.link ? `${article.link}#schema` : undefined,
            name: article.title,
            headline: article.title,
            description: article.summary || article.description,
            author: (article.authorNames && article.authorNames.length > 0)
              ? article.authorNames.map(name => ({ '@type': 'Person', name }))
              : { '@type': 'Person', name: article.authors || profile.name, url: profile.seo.url },
            publisher: article.journal
              ? { '@type': 'Periodical', name: article.journal, issn: article.journalMeta?.issn }
              : { '@type': 'Organization', name: article.affiliation || 'Eastern Michigan University' },
            ...(article.journal && {
              isPartOf: {
                '@type': 'Periodical',
                name: article.journal,
                issn: article.journalMeta?.issn,
              },
            }),
            datePublished: article.datePublished || new Date().toISOString().split('T')[0],
            url: article.link || url,
            mainEntityOfPage: article.link ? { '@type': 'WebPage', '@id': article.link } : undefined,
            ...(article.sameAs && article.sameAs.length > 0 && { sameAs: article.sameAs }),
            ...(article.doi && {
              identifier: [
                { '@type': 'PropertyValue', propertyID: 'DOI', value: article.doi },
              ],
            }),
          })}
        </script>
      ))}
    </Helmet>
  );
}

export function SEOProvider({ children }) {
  return <HelmetProvider>{children}</HelmetProvider>;
}


